import { useDraggable } from "@dnd-kit/core";
import { faCircleCheck } from "@fortawesome/free-solid-svg-icons/faCircleCheck";
import { faPenToSquare } from "@fortawesome/free-solid-svg-icons/faPenToSquare";
import { faRepeat } from "@fortawesome/free-solid-svg-icons/faRepeat";
import { faTrash } from "@fortawesome/free-solid-svg-icons/faTrash";
import React from "react";
import { useTranslation } from "react-i18next";
import styled, { css } from "styled-components";

import { AssigneeBanner } from "../../../components/AssigneeBanner";
import { AtiraIcon } from "../../../components/AtiraIcon";
import { Flex } from "../../../components/Flex";
import { FontSize, Text } from "../../../components/Text";
import { KanbanAutomationRule } from "../../../model/kanban/KanbanAutomationRule";
import { KanbanColumnAutomationActionType } from "../../../model/kanban/types/KanbanColumnAutomationActionType.enum";
import { User } from "../../../model/user/User";
import { Rounded } from "../../../theme/Rounded";
import { Spacing } from "../../../theme/Spacing";
import { Tooltip } from "../../../utils/local-antd";
import { kanbanAutomationActionMeta } from "../KanbanAutomationUtils";
import {
  KanbanCardActionButton,
  KanbanCardActionsWrapper,
  KnabanCardName,
  StyledKanbanCard,
} from "./KanbanCard";

const MAX_VISIBLE_RECIPIENTS = 2;

const RuleCard = styled(StyledKanbanCard)<{ $pending?: boolean }>`
  opacity: ${({ $pending }) => ($pending ? 0.6 : 1)};

  &:focus-within .kanban-card-actions {
    opacity: 1;
    pointer-events: auto;
  }
`;

const DeleteRuleButton = styled(KanbanCardActionButton)`
  color: ${({ theme }) => theme.danger};
`;

const Pill = styled(Flex)`
  flex-shrink: 0;
  align-items: center;
  padding: 0 ${Spacing.s};
  border-radius: ${Rounded.pill};
  border: 1px solid ${({ theme }) => theme.warning};
`;

// A plain element rather than a `Flex`: the tooltip hands its child ARIA props
// that `Flex` would turn into invalid inline styles.
const RecipientNames = styled.div`
  min-width: 0;
`;

const OverflowPill = styled(Flex)`
  flex-shrink: 0;
  align-items: center;
  padding: 0 ${Spacing.s};
  border-radius: ${Rounded.pill};
  background-color: ${({ theme }) => theme.sub};
`;

// Free text of any length: cut it to a fixed number of lines so it can never
// stretch the card.
const clampLines = (lines: number) => css`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: ${lines};
  overflow: hidden;
  overflow-wrap: anywhere;
`;

const TaskName = styled.p<{ $isDefault: boolean }>`
  margin: 0;
  font-size: ${FontSize.s};
  line-height: 1.4;
  font-weight: ${({ $isDefault }) => ($isDefault ? 400 : 700)};
  font-style: ${({ $isDefault }) => ($isDefault ? "italic" : "normal")};
  color: ${({ theme, $isDefault }) =>
    $isDefault ? theme.gray : theme.textColor};
  ${clampLines(1)}
`;

const TaskDescription = styled.p`
  margin: 0;
  font-size: ${FontSize.s};
  line-height: 1.4;
  color: ${({ theme }) => theme.gray};
  ${clampLines(2)}
`;

const DraggableRule = styled.div<{ $dragging: boolean }>`
  width: 100%;
  opacity: ${({ $dragging }) => ($dragging ? 0.4 : 1)};
  outline: none;
`;

type RecipientsProps = {
  recipientUserIds: string[];
  usersById: Map<string, User>;
  /** Shown when the list is empty - different for a rule with no recipients
   * versus a task left to fall back to the deal's own assignees. */
  emptyLabel: string;
  unresolvedLabel: string;
};

const Recipients: React.FC<RecipientsProps> = ({
  recipientUserIds,
  usersById,
  emptyLabel,
  unresolvedLabel,
}) => {
  if (!recipientUserIds.length) {
    return (
      <Text fontSize="s" color="gray">
        {emptyLabel}
      </Text>
    );
  }

  const recipients = recipientUserIds
    .map((id) => usersById.get(id))
    .filter((user): user is User => Boolean(user));

  // Members can be removed from the org after being picked, and the members
  // list can still be loading - fall back to a plain count.
  if (!recipients.length) {
    return (
      <Text fontSize="s" color="gray">
        {unresolvedLabel}
      </Text>
    );
  }

  const visible = recipients.slice(0, MAX_VISIBLE_RECIPIENTS);
  const overflow = recipientUserIds.length - visible.length;
  const allNames = recipients.map(({ name }) => name).join(", ");

  return (
    <Flex alignItems="center" gap="s">
      <AssigneeBanner hideSelf assignees={visible} title={allNames} />

      <Tooltip title={allNames}>
        <RecipientNames>
          <Text fontSize="s" cutText>
            {visible.map(({ name }) => name).join(", ")}
          </Text>
        </RecipientNames>
      </Tooltip>

      {overflow > 0 ? (
        <OverflowPill>
          {/* Fixed left-to-right so RTL text does not turn it into "N+". */}
          <Text fontSize="xs" direction="ltr">
            +{overflow}
          </Text>
        </OverflowPill>
      ) : null}
    </Flex>
  );
};

type TaskDetailsProps = {
  rule: KanbanAutomationRule;
  columnName: string;
  usersById: Map<string, User>;
};

const TaskDetails: React.FC<TaskDetailsProps> = ({
  rule,
  columnName,
  usersById,
}) => {
  const { t } = useTranslation();

  const name = rule.data?.name;
  const description = rule.data?.description;

  return (
    <Flex flexDirection="column" gap="xs">
      {name ? (
        <Tooltip title={name}>
          {/* Typed in any language, so it follows its own text direction. */}
          <TaskName $isDefault={false} dir="auto">
            {name}
          </TaskName>
        </Tooltip>
      ) : (
        // Left blank: this is the name the server gives the task.
        <TaskName $isDefault>
          {t("kanban.automation.rule.taskName.default", { column: columnName })}
        </TaskName>
      )}

      {description ? (
        <Tooltip title={description}>
          <TaskDescription dir="auto">{description}</TaskDescription>
        </Tooltip>
      ) : null}

      <Recipients
        recipientUserIds={rule.assigneeUserIds || []}
        usersById={usersById}
        emptyLabel={t("kanban.automation.rule.assignees.auto")}
        unresolvedLabel={t("kanban.automation.rule.assignees.unresolved", {
          total: rule.assigneeUserIds?.length,
        })}
      />
    </Flex>
  );
};

type KanbanAutomationRuleCardProps = {
  rule: KanbanAutomationRule;
  /** The column the rule sits in - a blank task name is derived from it. */
  columnName: string;
  /** Everyone a rule can name: recipients and whoever added it. */
  usersById: Map<string, User>;
  /** A change to this rule is in flight. */
  pending?: boolean;
  /** Without both handlers the hover actions are left out (drag overlay). */
  onEdit?: (rule: KanbanAutomationRule) => void;
  onDelete?: (rule: KanbanAutomationRule) => void;
};

/** A rule laid out like a kanban card, with its details visible at a glance. */
export const KanbanAutomationRuleCard: React.FC<
  KanbanAutomationRuleCardProps
> = ({ rule, columnName, usersById, pending, onEdit, onDelete }) => {
  const { t } = useTranslation();

  const meta = kanbanAutomationActionMeta[rule.actionType] || {};
  const addedBy = usersById.get(rule.createdBy)?.name;

  return (
    <RuleCard isParentDefault={false} $pending={pending}>
      <Flex flexDirection="column" gap="s" width={"100%"}>
        <Flex alignItems="center" justifyContent="space-between" gap="s">
          <Flex alignItems="center" gap="s" minWidth={0}>
            <AtiraIcon icon={meta?.icon} color="main" />
            <KnabanCardName cutText>{t(meta?.titleKey)}</KnabanCardName>
          </Flex>

          {rule.enabled ? null : (
            <Pill>
              <Text fontSize="xs" color="warning" fontWeight="bold">
                {t("kanban.automation.rule.paused")}
              </Text>
            </Pill>
          )}
        </Flex>

        <Flex alignItems="center" gap="s">
          <AtiraIcon
            icon={rule.repeatable ? faRepeat : faCircleCheck}
            color="gray"
            size="sm"
          />
          <Text fontSize="s" color="gray">
            {rule.repeatable
              ? t("kanban.automation.rule.repeat.everyTime")
              : t("kanban.automation.rule.repeat.oncePerDeal")}
          </Text>
        </Flex>

        {rule.actionType === KanbanColumnAutomationActionType.NOTIFICATION ? (
          <Recipients
            recipientUserIds={rule.recipientUserIds || []}
            usersById={usersById}
            emptyLabel={t("kanban.automation.rule.recipients.none")}
            unresolvedLabel={t("kanban.automation.rule.recipients.unresolved", {
              total: rule.recipientUserIds?.length,
            })}
          />
        ) : (
          <TaskDetails
            rule={rule}
            columnName={columnName}
            usersById={usersById}
          />
        )}

        <Flex alignItems="center" justifyContent="space-between" gap="s">
          <Flex minWidth={0}>
            {addedBy ? (
              <Text fontSize="xs" color="gray" cutText>
                {t("kanban.automation.rule.addedBy", { name: addedBy })}
              </Text>
            ) : null}
          </Flex>

          {onEdit && onDelete ? (
            <KanbanCardActionsWrapper className="kanban-card-actions">
              <KanbanCardActionButton
                icon={faPenToSquare}
                fontSize="m"
                info={t("common.edit")}
                aria-label={t("common.edit")}
                disabled={pending}
                onClick={() => onEdit(rule)}
              />
              <DeleteRuleButton
                icon={faTrash}
                fontSize="m"
                info={t("common.delete")}
                aria-label={t("common.delete")}
                disabled={pending}
                onClick={() => onDelete(rule)}
              />
            </KanbanCardActionsWrapper>
          ) : null}
        </Flex>
      </Flex>
    </RuleCard>
  );
};

type DraggableKanbanAutomationRuleCardProps = KanbanAutomationRuleCardProps & {
  /** The lane the card is currently shown in. */
  columnId: string;
};

/** A rule card that can be picked up and dropped into another lane. */
export const DraggableKanbanAutomationRuleCard: React.FC<
  DraggableKanbanAutomationRuleCardProps
> = ({ columnId, ...cardProps }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: cardProps.rule._id,
    data: { columnId },
    // A move that has not settled yet must not be raced by another one.
    disabled: cardProps.pending,
    // Only the mouse and touch sensors are wired up, so this is not a tab stop
    // of its own - the edit/delete buttons inside are.
    attributes: { role: "group", tabIndex: -1 },
  });

  return (
    <DraggableRule
      ref={setNodeRef}
      $dragging={isDragging}
      {...attributes}
      {...listeners}
    >
      <KanbanAutomationRuleCard {...cardProps} />
    </DraggableRule>
  );
};
